import type { Neighborhood } from "../../types/neighborhood";
import type { FetchServiceAreaPlacesParams } from "./fetchServiceAreaPlaces";
import {
  loadGoogleMapsPlaces,
  readGooglePlaceLocation,
  type GooglePlacePrediction,
  type GooglePlacesLibrary,
} from "./googleMapsPlacesLoader";
import { sortNeighborhoodsByDistance } from "./neighborhoodDisplay";
import { promiseWithTimeout } from "./promiseWithTimeout";

const MAX_RESULTS = 8;
const AUTOCOMPLETE_TIMEOUT_MS = 6_000;
const PLACE_DETAILS_TIMEOUT_MS = 4_000;
const SEARCH_TIMEOUT_MS = 12_000;

const AREA_PRIMARY_TYPES = [
  "locality",
  "sublocality",
  "administrative_area_level_3",
] as const;

function predictionLabel(prediction: GooglePlacePrediction): string {
  const main = prediction.mainText?.text?.trim();
  if (main) {
    return main;
  }

  const text = prediction.text?.text?.trim() ?? "";
  return text.split(",")[0]?.trim() || text;
}

function regionLabelFromPrediction(
  prediction: GooglePlacePrediction,
): string | undefined {
  const secondary = prediction.secondaryText?.text?.trim();
  if (secondary) {
    return secondary;
  }

  const full = prediction.text?.text?.trim() ?? "";
  const main = predictionLabel(prediction);
  if (!full || !main || full.length <= main.length) {
    return undefined;
  }

  const suffix = full.slice(main.length).replace(/^,\s*/, "").trim();
  return suffix || undefined;
}

function isExcluded(name: string, excludeNames: string[]): boolean {
  const key = name.trim().toLowerCase();
  return excludeNames.some((entry) => entry.trim().toLowerCase() === key);
}

function predictionSortDistance(prediction: GooglePlacePrediction): number {
  if (typeof prediction.distanceMeters === "number") {
    return prediction.distanceMeters;
  }
  return Number.POSITIVE_INFINITY;
}

async function resolveNeighborhood(
  prediction: GooglePlacePrediction,
  center: { lat: number; lng: number } | null,
): Promise<Neighborhood | null> {
  const name = predictionLabel(prediction);
  if (!name) {
    return null;
  }

  const place = prediction.toPlace();
  try {
    await promiseWithTimeout(
      place.fetchFields({ fields: ["location", "displayName"] }),
      PLACE_DETAILS_TIMEOUT_MS,
      "Place details timed out",
    );
  } catch {
    return null;
  }

  const coords = readGooglePlaceLocation(place) ?? center;
  if (!coords) {
    return null;
  }

  const regionLabel = regionLabelFromPrediction(prediction);

  return {
    name,
    lat: coords.lat,
    lng: coords.lng,
    placeId: prediction.placeId,
    regionLabel,
  };
}

function buildAutocompleteRequest(
  params: FetchServiceAreaPlacesParams,
  sessionToken: object,
): Parameters<
  GooglePlacesLibrary["AutocompleteSuggestion"]["fetchAutocompleteSuggestions"]
>[0] {
  const request: Parameters<
    GooglePlacesLibrary["AutocompleteSuggestion"]["fetchAutocompleteSuggestions"]
  >[0] = {
    input: params.query,
    sessionToken,
    includedPrimaryTypes: [...AREA_PRIMARY_TYPES],
  };

  if (params.center) {
    request.origin = params.center;
  }

  return request;
}

async function fetchServiceAreaPlacesClientInner(
  params: FetchServiceAreaPlacesParams & { googleMapsApiKey: string },
): Promise<Neighborhood[]> {
  const placesLib = await loadGoogleMapsPlaces(params.googleMapsApiKey);
  const sessionToken = new placesLib.AutocompleteSessionToken();
  const request = buildAutocompleteRequest(params, sessionToken);

  const { suggestions } = await promiseWithTimeout(
    placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request),
    AUTOCOMPLETE_TIMEOUT_MS,
    "Places autocomplete timed out",
  );

  const candidates = suggestions
    .map((suggestion) => suggestion.placePrediction)
    .filter(
      (prediction): prediction is GooglePlacePrediction => prediction != null,
    )
    .filter(
      (prediction) =>
        !isExcluded(predictionLabel(prediction), params.excludeNames),
    )
    .sort((a, b) => predictionSortDistance(a) - predictionSortDistance(b));

  if (candidates.length === 0) {
    return [];
  }

  const neighborhoods = (
    await Promise.all(
      candidates.map((prediction) =>
        resolveNeighborhood(prediction, params.center),
      ),
    )
  ).filter((n): n is Neighborhood => n != null);

  return sortNeighborhoodsByDistance(neighborhoods, params.center).slice(
    0,
    MAX_RESULTS,
  );
}

export async function fetchServiceAreaPlacesClient(
  params: FetchServiceAreaPlacesParams & { googleMapsApiKey: string },
): Promise<Neighborhood[]> {
  return promiseWithTimeout(
    fetchServiceAreaPlacesClientInner(params),
    SEARCH_TIMEOUT_MS,
    "Area search timed out",
  );
}
